/**
 * Agents Overview panel.
 *
 * Rendered on the CEO Dashboard when the first scope-bar dropdown is
 * set to "Agents Dashboard Overview". Two modes:
 *
 *   1. No agent selected (sliceBy = "agents"):
 *      ─ Summary strip (Total Agents · Leads · Active · Revenue ·
 *        Calls · Missed Calls)
 *      ─ Table of every agent in the org with their headline KPIs,
 *        sorted by revenue desc
 *
 *   2. One agent selected (sliceBy = "agent:<id>"):
 *      ─ Hero card with the agent's avatar + name + role + KPIs
 *      ─ Either: a single-row table (with_team = false), OR
 *        a team-mate table when "With Team" is checked
 *
 * Backed by GET /api/leadership/agents-overview which streams the
 * aggregated per-agent data.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users, Phone, PhoneMissed, ClipboardCheck, Wallet,
  TrendingUp, Star, Clock, MapPin, ArrowDownAZ,
} from 'lucide-react';
import HelpInfo from '@/components/HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtInt = (n) => (n == null ? '0' : Number(n).toLocaleString('en-AE'));

const fmtAED = (n) => {
  if (n == null || Number.isNaN(Number(n))) return 'AED 0';
  const v = Math.round(Number(n));
  if (Math.abs(v) >= 1_000_000) return `AED ${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `AED ${(v / 1_000).toFixed(1)}K`;
  return `AED ${v.toLocaleString('en-AE')}`;
};

const fmtDuration = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return [hh, mm, ss].map((v) => String(v).padStart(2, '0')).join(':');
};

const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const initialsBg = (name) => {
  const palette = [
    'bg-slate-200 text-slate-700',
    'bg-violet-200 text-violet-700',
    'bg-amber-200 text-amber-700',
    'bg-emerald-200 text-emerald-700',
    'bg-rose-200 text-rose-700',
    'bg-blue-200 text-blue-700',
  ];
  let h = 0;
  for (const c of String(name || '')) h = (h + c.charCodeAt(0)) % palette.length;
  return palette[h];
};

const prettyRole = (r) =>
  !r ? 'Agent' : String(r).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const lastActiveLabel = (iso) => {
  if (!iso) return 'Never logged in';
  try {
    const then = new Date(iso).getTime();
    const now  = Date.now();
    const mins = Math.floor((now - then) / 60000);
    if (mins < 1) return 'Active now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / 1440)}d ago`;
  } catch (_) { return ''; }
};

// ============================================================
// Summary strip — six tiles
// ============================================================
const SUMMARY_TILES = [
  { key: 'total_agents',    icon: Users,           label: 'Active Agents',  fmt: fmtInt },
  { key: 'total_leads',     icon: ClipboardCheck,  label: 'Leads Owned',    fmt: fmtInt },
  { key: 'total_active',    icon: Star,            label: 'In Pursuit',     fmt: fmtInt },
  { key: 'total_revenue',   icon: Wallet,          label: 'Revenue',        fmt: fmtAED },
  { key: 'total_calls',     icon: Phone,           label: 'Calls Made',     fmt: fmtInt },
  { key: 'total_missed',    icon: PhoneMissed,     label: 'Missed Calls',   fmt: fmtInt, danger: true },
];

const SummaryStrip = ({ summary }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
    {SUMMARY_TILES.map((t) => {
      const Icon = t.icon;
      const v = summary?.[t.key];
      const danger = t.danger && v > 0;
      return (
        <div key={t.key}
             className={`rounded-xl border p-3 bg-white transition hover:shadow-sm ${
               danger ? 'border-rose-200' : 'border-slate-200'
             }`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
              danger ? 'bg-rose-50 text-rose-600' : 'bg-slate-50 text-slate-500'
            }`}>
              <Icon size={13} />
            </div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">
              {t.label}
            </p>
          </div>
          <p className={`text-xl font-bold tabular-nums ${danger ? 'text-rose-600' : 'text-slate-900'}`}>
            {t.fmt(v)}
          </p>
        </div>
      );
    })}
  </div>
);

// ============================================================
// Hero card — single agent's headline view
// ============================================================
const AgentHeroCard = ({ agent }) => {
  if (!agent) return null;
  return (
    <Card className="p-5 border-slate-200 relative overflow-hidden bg-gradient-to-br from-violet-50/70 via-white to-blue-50/40">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="relative flex flex-col md:flex-row gap-5">
        <div className="flex items-center gap-3 md:w-72">
          <Avatar className="w-16 h-16">
            <AvatarFallback className={`text-base font-bold ${initialsBg(agent.agent_name)}`}>
              {initials(agent.agent_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-lg font-bold text-slate-900 truncate">{agent.agent_name}</p>
            <p className="text-xs text-slate-500 truncate">{prettyRole(agent.agent_role)}</p>
            <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
              <Clock size={10} /> {lastActiveLabel(agent.last_active)}
            </p>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-3 md:grid-cols-6 gap-3">
          <Stat label="Leads"        value={fmtInt(agent.leads_owned)}  />
          <Stat label="Active"       value={fmtInt(agent.active_leads)} />
          <Stat label="Deals Won"    value={fmtInt(agent.won_deals)} tint="emerald" />
          <Stat label="Revenue"      value={fmtAED(agent.won_revenue)} tint="emerald" />
          <Stat label="Conv. Rate"   value={`${(agent.conversion_pct ?? 0).toFixed(1)}%`} />
          <Stat label="Talk Time"    value={fmtDuration(agent.talk_seconds)} mono />
        </div>
      </div>
    </Card>
  );
};

const Stat = ({ label, value, tint, mono }) => {
  const tone = tint === 'emerald' ? 'text-emerald-700' : 'text-slate-900';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-base font-bold ${tone} ${mono ? 'font-mono' : ''} tabular-nums mt-0.5`}>
        {value}
      </p>
    </div>
  );
};

// ============================================================
// Agents table
// ============================================================
const AgentsTable = ({ agents = [], highlightId }) => {
  if (agents.length === 0) {
    return (
      <Card className="p-8 border-slate-200 text-center">
        <Users size={32} className="text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
        <p className="text-sm font-medium text-slate-700">No agents found</p>
        <p className="text-xs text-slate-500 mt-1">
          Add sales / marketing agents under Settings → Users to populate this view.
        </p>
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200 bg-slate-50/50">
            <tr>
              <th className="text-left  py-2.5 px-3 font-semibold">
                <span className="inline-flex items-center gap-1">
                  <ArrowDownAZ size={11} /> Agent
                </span>
              </th>
              <th className="text-right py-2.5 px-3 font-semibold">Leads</th>
              <th className="text-right py-2.5 px-3 font-semibold">Active</th>
              <th className="text-right py-2.5 px-3 font-semibold">Won</th>
              <th className="text-right py-2.5 px-3 font-semibold">Revenue</th>
              <th className="text-right py-2.5 px-3 font-semibold">Site Visits</th>
              <th className="text-right py-2.5 px-3 font-semibold">Calls</th>
              <th className="text-right py-2.5 px-3 font-semibold text-rose-600">Missed</th>
              <th className="text-right py-2.5 px-3 font-semibold">Talk Time</th>
              <th className="text-right py-2.5 px-3 font-semibold">Conv %</th>
              <th className="text-right py-2.5 px-3 font-semibold">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a, i) => {
              const isFocus = highlightId && a.agent_id === highlightId;
              return (
                <tr key={a.agent_id || i}
                    className={`border-b border-slate-100 last:border-0 transition ${
                      isFocus ? 'bg-violet-50/70 ring-1 ring-violet-200' : 'hover:bg-slate-50/60'
                    }`}>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono text-slate-400 w-5 text-right">
                        #{i + 1}
                      </span>
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className={`text-[11px] font-semibold ${initialsBg(a.agent_name)}`}>
                          {initials(a.agent_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{a.agent_name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{prettyRole(a.agent_role)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-right text-slate-800 tabular-nums">{fmtInt(a.leads_owned)}</td>
                  <td className="py-3 px-3 text-right text-slate-800 tabular-nums">{fmtInt(a.active_leads)}</td>
                  <td className="py-3 px-3 text-right tabular-nums">
                    <span className={a.won_deals > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-400'}>
                      {fmtInt(a.won_deals)}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right tabular-nums">
                    <span className={a.won_revenue > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-400'}>
                      {fmtAED(a.won_revenue)}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right text-slate-800 tabular-nums">{fmtInt(a.site_visits)}</td>
                  <td className="py-3 px-3 text-right text-slate-800 tabular-nums">{fmtInt(a.calls_made)}</td>
                  <td className={`py-3 px-3 text-right tabular-nums ${a.missed_calls > 0 ? 'text-rose-600 font-medium' : 'text-slate-400'}`}>
                    {fmtInt(a.missed_calls)}
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-xs text-slate-700">
                    {fmtDuration(a.talk_seconds)}
                  </td>
                  <td className="py-3 px-3 text-right tabular-nums">
                    <span className={`${
                      a.conversion_pct >= 15 ? 'text-emerald-600' :
                      a.conversion_pct >= 5  ? 'text-amber-600' :
                                                'text-slate-400'
                    } font-medium`}>
                      {(a.conversion_pct ?? 0).toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right text-[11px] text-slate-500">
                    {lastActiveLabel(a.last_active)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ============================================================
// Main panel
// ============================================================
export default function AgentsOverviewPanel({ agentId = null, withTeam = false }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { token };
      if (agentId)  params.agent_id = agentId;
      if (withTeam) params.with_team = 'true';
      const r = await axios.get(`${API}/leadership/agents-overview`, { params });
      setData(r.data?.data || null);
    } catch (e) {
      console.warn('agents-overview:', e?.response?.data?.detail || e);
    } finally {
      setLoading(false);
    }
  }, [token, agentId, withTeam]);
  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const agents  = data?.agents || [];
  const focus   = agentId ? agents.find((a) => a.agent_id === agentId) : null;

  return (
    <Card className="border-slate-200 p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-sm">
          <Users size={16} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">
            {agentId
              ? `Agent Drill-in${withTeam ? ' + Team' : ''}`
              : 'Agents Overview'}
          </h3>
          <p className="text-[11px] text-slate-500">
            {agentId
              ? (withTeam
                  ? 'Selected agent and everyone on their team'
                  : 'Single-agent KPI snapshot')
              : 'Every individual contributor in the org with their headline KPIs'}
          </p>
        </div>
        <HelpInfo
          align="inline"
          title="Agents Overview"
          steps={[
            'Default view shows a sortable table of every agent — sorted by revenue desc — with leads, active, deals won, revenue, site visits, calls, missed calls, talk time, and conversion %.',
            'Pick a specific agent from the second dropdown to drill into their dashboard. Their row is highlighted in the table.',
            'Tick "With Team" to also see their team-mates (rows sharing the same team_id).',
            'Missed Calls is shown in red — a high count is the strongest signal an agent is dropping inbound buyers.',
          ]}
          tip="Sort the table mentally by Missed Calls: a low-revenue + high-missed combination is your highest-priority coaching candidate."
        />
        {loading && <span className="ml-auto text-[11px] text-slate-400">Refreshing…</span>}
      </div>

      {/* Loading skeletons */}
      {loading && !data ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
          </div>
          <Skeleton className="h-72 w-full rounded-xl" />
        </div>
      ) : (
        <>
          {/* Hero card when one agent is selected */}
          {focus && <AgentHeroCard agent={focus} />}

          {/* Summary strip — shown for both modes */}
          <SummaryStrip summary={summary} />

          {/* Table */}
          <AgentsTable agents={agents} highlightId={agentId} />
        </>
      )}
    </Card>
  );
}
